import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import { TextArea } from 'devextreme-react';
import { Button } from 'devextreme-react/button';
import { LoadPanel } from 'devextreme-react/load-panel';
import { sanitizeHtml } from './sanitizeHtml';

/**
 * FeedbackModal - A reusable feedback modal/component
 * 
 * @param {Object} props
 * @param {boolean} props.visible - Controls visibility of the component
 * @param {Object} props.feedbackData - Object containing { userId, text, action }
 * @param {string} props.accessToken - Access token for API calls
 * @param {Function} props.onClose - Callback when component is closed
 * @param {string} props.apiEndpoint - API endpoint for submitting feedback (default: '/Identity/SendFeedBackEmail')
 * @param {boolean} props.asModal - If true, renders as modal overlay; if false, renders as inline form (default: true)
 * @param {Object} props.containerStyle - Custom styles for the container (when asModal is false)
 */
const FeedbackModal = ({ 
  visible, 
  feedbackData, 
  accessToken, 
  onClose,
  apiEndpoint = '/Identity/SendFeedBackEmail',
  asModal = true,
  containerStyle = {}
}) => {
  const [comments, setComments] = useState("");
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const contentRef = useRef(null);

  const handleClose = () => {
    setComments("");
    setIsSubmitting(false);
    setSubmitSuccess(false);
    setErrorMessage("");
    if (onClose) {
      onClose();
    }
  };

  const handleSubmit = async () => {
    if (!feedbackData) return;
    
    setIsSubmitting(true);
    setErrorMessage("");
    setSubmitSuccess(false);
    
    try {
      const response = await axios.post(
        `${window._env_.REACT_APP_AUTH_URL}${apiEndpoint}`,
        {
          UserId: feedbackData.userId,
          Text: feedbackData.text,
          Comments: comments,
          Action: feedbackData.action
        },
        {
          headers: {
            "Content-Type": "application/json",
            ...(accessToken ? { access_token: accessToken } : {}),
          },
        }
      );

      if (response.data && response.data.result === true) {
        setSubmitSuccess(true);
        setTimeout(() => {
          handleClose();
        }, 2000);
      } else {
        setErrorMessage("Failed to submit feedback. Please try again.");
        setTimeout(() => {
          handleClose();
        }, 3000);
      }
    } catch (error) {
      console.error("Error submitting feedback:", error);
      const errorMsg = error.response?.data?.message || 
                       error.response?.data?.error || 
                       error.message || 
                       "Failed to submit feedback. Please try again.";
      setErrorMessage(errorMsg);
      setTimeout(() => {
        handleClose();
      }, 3000);
    } finally {
      setIsSubmitting(false);
    }
  };


  if (!visible || !feedbackData) {
    return null;
  }

  const overlayStyle = asModal ? {
    position: "fixed",
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    backgroundColor: "rgba(0, 0, 0, 0.5)",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    zIndex: 10000
  } : {};

  const contentStyle = asModal ? {
    backgroundColor: "#fff",
    borderRadius: "8px",
    padding: "1.5rem",
    maxWidth: "500px",
    width: "90%",
    maxHeight: "80vh",
    overflow: "auto",
    boxShadow: "0 4px 12px rgba(0,0,0,0.3)"
  } : {
    flex: 1,
    display: "flex",
    flexDirection: "column",
    padding: "1rem",
    backgroundColor: "#fff",
    overflow: "auto",
    ...containerStyle
  };

  return (
    <div style={overlayStyle}>
      {isSubmitting && (
        <LoadPanel
          visible={true}
          showIndicator={true}
          showPane={true}
          shading={asModal}
          shadingColor="rgba(0,0,0,0.4)"
          hideOnOutsideClick={false}
          position={{ of: window }}
        />
      )}
      <div ref={contentRef} style={contentStyle}>
        <h3 style={{ margin: "0 0 1rem 0", color: "#333" }}>
          {feedbackData.action === "Like" ? "👍 Like Feedback" : "👎 Dislike Feedback"}
        </h3>
        
        <div style={{ marginBottom: "1rem" }}>
          <label style={{ display: "block", marginBottom: "0.5rem", fontWeight: "bold", color: "#333" }}>
            Answer:
          </label>
          <div style={{
            padding: "0.75rem",
            backgroundColor: "#f5f5f5",
            borderRadius: "4px",
            border: "1px solid #ddd",
            maxHeight: "200px",
            overflow: "auto",
            color: "#333",
            fontSize: "0.9rem"
          }}>
            <div dangerouslySetInnerHTML={{ __html: sanitizeHtml(feedbackData.text) }} />
          </div>
        </div>

        <div style={{ marginBottom: "1rem" }}>
          <TextArea
            value={comments}
            onValueChanged={(e) => setComments(e.value || "")}
            placeholder="Please provide your feedback..."
            label="Comments"
            labelMode="static"
            stylingMode="outlined"
            height={120}
          />
        </div>

        {submitSuccess && (
          <div style={{
            padding: "0.75rem",
            backgroundColor: "#d4edda",
            color: "#155724",
            borderRadius: "4px",
            marginBottom: "1rem",
            border: "1px solid #c3e6cb",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}>
            <span>✓</span>
            <span>Feedback submitted successfully!</span>
          </div>
        )}

        {errorMessage && (
          <div style={{
            padding: "0.75rem",
            backgroundColor: "#f8d7da",
            color: "#721c24",
            borderRadius: "4px",
            marginBottom: "1rem",
            border: "1px solid #f5c6cb",
            display: "flex",
            alignItems: "center",
            gap: "0.5rem"
          }}>
            <span>✗</span>
            <span>{errorMessage}</span>
          </div>
        )}

        <div style={{ display: "flex", gap: "0.5rem", justifyContent: "flex-end" }}>
          <Button
            text="Close"
            onClick={handleClose}
            disabled={isSubmitting}
          />
          <Button
            text="Submit"
            type="default"
            onClick={handleSubmit}
            disabled={isSubmitting || submitSuccess}
          />
        </div>
      </div>
    </div>
  );
};

export default FeedbackModal;

